/**
 * Whitespace made visible: a dot per space, a return mark per line break.
 *
 * A slot's leading and trailing spaces are what separate it from the words
 * around it once the body composes, so they are content — but a space at the
 * edge of a card or a field is invisible, and one the author adds by accident is
 * just as invisible. The same goes for a line break, which otherwise shows only
 * as the absence of text. Both are drawn in a muted tone without touching the
 * value: the text itself is never rewritten, only marked.
 */

import React, { useLayoutEffect, useRef } from 'react';
import styles from './slots.module.css';

/**
 * The text, with its whitespace marked.
 *
 * Each space or line break is wrapped rather than replaced, and the mark is
 * painted over that wrapper by CSS. So the text still measures exactly as it
 * would unmarked — which is what lets a mirror sit behind a field, marking
 * the field's own text — and each mark places itself against the
 * character it belongs to instead of guessing at where that is.
 */
export const InvisibleCharShower: React.FC<{ text: string }> = ({ text }) => (
  <>
    {text.split(/([ \n])/).map((part, i) => {
      if (part === ' ') {
        return (
          <span key={i} className={styles.spaceDot} data-space-dot="">
            {' '}
          </span>
        );
      }
      if (part === '\n') {
        return (
          <span key={i} className={styles.lineBreakMark} data-line-break="">
            {'\n'}
          </span>
        );
      }
      return <React.Fragment key={i}>{part}</React.Fragment>;
    })}
  </>
);

/**
 * A text field that shows its spaces. The control and a mirror of its value
 * share one grid cell: the control draws the text, the caret and the
 * selection, and the mirror behind it draws only the marks — its own glyphs are
 * transparent, so all that shows of it is a dot under each space and a return
 * mark after each line break.
 *
 * The text the author sees is the text the control lays out, so the caret is
 * always on it. The marks land on their characters only as long as the mirror
 * wraps and spaces its lines like the control, which is why the control is a
 * `<textarea>` (an `<input>` scrolls sideways instead of wrapping; fields that
 * want one line use `InvisibleCharShowingLine`) and why the fields that carry
 * this keep a whole-pixel line height, which every engine lays out alike.
 *
 * The control grows to fit its own text rather than trusting the mirror's
 * height, so if the two ever disagree a mark may sit a little off, but no text
 * is clipped or scrolled out of view.
 */
export const InvisibleCharShowingField: React.FC<{
  value: string;
  className: string;
  children: React.ReactNode;
}> = ({ value, className, children }) => {
  const cellRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const control = cellRef.current?.querySelector('textarea');
    if (!control) return;
    const fit = () => {
      control.style.minHeight = '';
      const borders = control.offsetHeight - control.clientHeight;
      control.style.minHeight = `${control.scrollHeight + borders}px`;
    };
    fit();
    if (typeof ResizeObserver === 'undefined') return;
    // A width change rewraps the text, so the height it needs changes too.
    let width = control.clientWidth;
    const observer = new ResizeObserver(() => {
      if (control.clientWidth === width) return;
      width = control.clientWidth;
      fit();
    });
    observer.observe(control);
    return () => observer.disconnect();
  }, [value]);

  return (
    <span ref={cellRef} className={styles.invisibleCharField}>
      <span
        className={`${className} ${styles.invisibleCharMirror}`}
        aria-hidden="true"
      >
        <InvisibleCharShower text={value} />
      </span>
      {children}
    </span>
  );
};

/**
 * A one-line value in an `InvisibleCharShowingField`: a one-row textarea that
 * grows as its value wraps and never takes a line break. Enter leaves the
 * field, and a pasted line break becomes a space.
 */
export const InvisibleCharShowingLine: React.FC<
  Omit<
    React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    'value' | 'onChange' | 'className'
  > & {
    value: string;
    className: string;
    onChange: (value: string) => void;
    'data-testid'?: string;
  }
> = ({ value, className, onChange, ...rest }) => (
  <InvisibleCharShowingField value={value} className={className}>
    <textarea
      {...rest}
      rows={1}
      value={value}
      enterKeyHint="done"
      onChange={e => onChange(e.target.value.replace(/\r?\n/g, ' '))}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.blur();
        }
      }}
      className={`${className} ${styles.invisibleCharFieldInput} ${styles.invisibleCharLine}`}
    />
  </InvisibleCharShowingField>
);
